import { DefaultProjectFormData, useGetTeamsTemplates } from '@common/projectHooks';
import { BackEndCodeLanguage } from '@prophecy/graphql-types/enums';
import { PlusIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Text, Stack, Button, Select, Dialog } from '@prophecy/ui-v3';
import { useIsFirstRender } from '@prophecy/utils/react/hooks';
import { useSupportedLanguages } from 'frontend/core/src/context/edition';
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import DefaultProjectSettings, { TemplateCreationMode } from './DefaultProjectSettings';

const StyledContainer = styled(Stack)`
  padding: ${theme.spaces.x4} ${theme.spaces.x24};
  overflow-y: auto;
  height: 100%;
`;

const AddButtonContainer = styled(Stack)`
  margin-top: ${theme.spaces.x24};
`;

export const TemplateCreation = ({ teamId }: { teamId: string }) => {
  const { getTeamsTemplates } = useGetTeamsTemplates();
  const isFirstRender = useIsFirstRender();
  const supportedLanguages = useSupportedLanguages();
  const fetchTemplates = useCallback(() => {
    getTeamsTemplates(teamId).then((data) => {
      const templates = data.TeamProjectCreationTemplates.filter((templete) => {
        if (templete.allowToCustomize) {
          return true;
        }
        return supportedLanguages.has(templete.language as BackEndCodeLanguage);
      });
      setTemplateData(templates);
      if (isFirstRender) {
        let defaultTemplate = templates.find((template) => template.isDefault);
        if (!defaultTemplate) {
          defaultTemplate = templates[0];
        }
        setSelectedTemplate(defaultTemplate);
      }
    });
  }, [getTeamsTemplates, teamId, isFirstRender, supportedLanguages]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const [templateData, setTemplateData] = useState<DefaultProjectFormData[]>([]);

  const [selectedTemplate, setSelectedTemplate] = useState<DefaultProjectFormData | null>(null);
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);

  return (
    <StyledContainer>
      <Stack direction='vertical' gap={theme.spaces.x12}>
        <Stack width='100%' direction='horizontal' gap={theme.spaces.x12}>
          <Select
            options={templateData?.map((template) => ({
              label: template.name,
              value: template.uid
            }))}
            label='Templates'
            data-test-id='admin-template-creation-select'
            style={{ flex: 1 }}
            onChange={(value) =>
              setSelectedTemplate(Object.assign({}, templateData?.find((template) => template.uid === value) || null))
            }
            value={selectedTemplate?.uid}
          />
          <AddButtonContainer direction='vertical' gap={theme.spaces.x12}>
            <Button
              iconPlacement='left'
              icon={<PlusIcon type='solid' />}
              onClick={() => setIsAddingTemplate(true)}
              data-test-id='template-creation-add-new-button'>
              Add New
            </Button>
          </AddButtonContainer>
        </Stack>

        <Stack direction='vertical' gap={theme.spaces.x12} width='100%'>
          {selectedTemplate ? (
            <DefaultProjectSettings
              fetchedDefaultValues={selectedTemplate}
              mode={TemplateCreationMode.Edit}
              teamId={teamId}
              uid={selectedTemplate?.uid}
              closeDialog={() => setIsAddingTemplate(false)}
              onTemplateUpdate={fetchTemplates}
            />
          ) : (
            <Text level='md'>Select a template</Text>
          )}
        </Stack>

        <Dialog
          data-test-id='template-creation-dialog'
          size='xxl'
          okButton={null}
          closable
          showCloseIcon
          closeButton={null}
          open={isAddingTemplate}
          onClose={() => setIsAddingTemplate(false)}>
          <DefaultProjectSettings
            teamId={teamId}
            closeDialog={() => setIsAddingTemplate(false)}
            mode={TemplateCreationMode.Add}
            onTemplateUpdate={fetchTemplates}
          />
        </Dialog>
      </Stack>
    </StyledContainer>
  );
};
